import React, { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import * as FiIcons from 'react-icons/fi';
import SafeIcon from '../common/SafeIcon';
import AuthModal from './AuthModal';
import stripeService from '../services/stripeService';
import supabase from '../supabase/supabase';

const { FiX, FiCreditCard, FiCheck, FiStar, FiAlertCircle, FiUser, FiZap, FiDatabase, FiSliders, FiPlusCircle, FiFileText, FiMessageCircle } = FiIcons;

const PaymentModal = ({ isOpen, onClose, onPaymentSuccess, remainingCalculations, user, onUserUpdate }) => {
  const [isProcessing, setIsProcessing] = useState(false);
  const [paymentComplete, setPaymentComplete] = useState(false);
  const [paymentError, setPaymentError] = useState(null);
  const [showAuthModal, setShowAuthModal] = useState(false);
  const [authMode, setAuthMode] = useState('signup');

  useEffect(() => {
    if (isOpen) {
      setIsProcessing(false);
      setPaymentComplete(false);
      setPaymentError(null);
    }
  }, [isOpen]);

  const proceedToStripe = async (currentUser) => {
    try {
      setIsProcessing(true);
      setPaymentError(null);
      console.log('Starting payment process for user:', currentUser.email);
      const success = stripeService.openCheckoutPage();
      if (!success) {
        throw new Error('Failed to redirect to payment page');
      }
    } catch (error) {
      console.error('Payment error:', error);
      setIsProcessing(false);
      setPaymentError(`Payment failed: ${error.message}. Please try again.`);
    }
  };

  const handleStripePayment = async () => {
    if (!user) {
      setAuthMode('signup');
      setShowAuthModal(true);
      return;
    }
    await proceedToStripe(user);
  };

  const handleAuthSuccess = async (newUser) => {
    setShowAuthModal(false);
    if (onUserUpdate) {
      const { data: profile } = await supabase
        .from('profiles')
        .select('*')
        .eq('id', newUser.id)
        .single();
      onUserUpdate({ ...newUser, ...profile });
    }
    await proceedToStripe(newUser);
  };
  
  const handlePaymentSuccess = () => {
    setIsProcessing(false);
    setPaymentComplete(true);
    setTimeout(() => {
      onClose();
    }, 2000);
  };

  useEffect(() => {
    if (paymentComplete) {
      onPaymentSuccess();
    }
  }, [paymentComplete, onPaymentSuccess]);

  const proFeatures = [
    { 
      icon: FiZap, 
      title: 'Create Limitless Plans', 
      description: 'Never worry about running out. Optimize as many meals as you need to perfect your diet.' 
    },
    { 
      icon: FiSliders, 
      title: 'Precision Macro Targeting', 
      description: 'Go beyond just calories. Dial in your protein, carbs, and fat to meet specific goals.' 
    },
    { 
      icon: FiDatabase, 
      title: 'Massive Food Database', 
      description: 'Access over 500,000+ food items to build any meal you can imagine.' 
    },
    { 
      icon: FiPlusCircle, 
      title: 'Add Your Own Foods', 
      description: 'Your favorite local ingredients or custom recipes are just a click away.' 
    },
    { 
      icon: FiFileText, 
      title: 'Professional PDF Exports', 
      description: 'Save, print, or share beautifully formatted meal plans for easy reference.' 
    },
    { 
      icon: FiMessageCircle, 
      title: 'Get Priority Support', 
      description: 'Jump to the front of the line with dedicated help whenever you need it.' 
    }
  ];

  if (!isOpen) return null;

  return (
    <>
      <motion.div
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
        className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50"
        onClick={onClose}
      >
        <motion.div
          initial={{ scale: 0.9, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          exit={{ scale: 0.9, opacity: 0 }}
          className="bg-white rounded-2xl shadow-xl w-full max-w-md max-h-[90vh] flex flex-col"
          onClick={(e) => e.stopPropagation()}
        >
          {paymentComplete ? (
            <div className="p-6 text-center">
              <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <SafeIcon icon={FiCheck} className="text-2xl text-green-600" />
              </div>
              <h3 className="text-2xl font-bold text-gray-800 mb-2">Payment Successful!</h3>
              <p className="text-gray-600 mb-4">
                Thank you for upgrading! You now have unlimited access to the meal calculator.
              </p>
              <div className="bg-green-50 border border-green-200 rounded-lg p-3">
                <p className="text-green-800 font-semibold">🎉 Unlimited meal calculations unlocked!</p>
              </div>
            </div>
          ) : (
            <>
              <div className="p-6 border-b border-gray-200 flex justify-between items-center flex-shrink-0">
                <h2 className="text-2xl font-bold text-gray-800 flex items-center gap-2">
                  <SafeIcon icon={FiStar} className="text-yellow-500" /> Upgrade to Pro
                </h2>
                <button onClick={onClose} className="text-gray-500 hover:text-gray-700 p-1 rounded-full hover:bg-gray-100">
                  <SafeIcon icon={FiX} className="text-xl" />
                </button>
              </div>
              <div className="p-6 space-y-6 overflow-y-auto">
                <div className="bg-orange-50 border border-orange-200 rounded-lg p-4">
                  <h3 className="font-semibold text-orange-800 mb-2">Free Trial Used Up!</h3>
                  <p className="text-orange-700 text-sm">
                    You've successfully completed {3 - remainingCalculations} optimized meal calculations within your target thresholds. To continue using the meal calculator, please upgrade to Pro.
                  </p>
                </div>

                {user ? (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center gap-2 mb-2">
                      <SafeIcon icon={FiUser} className="text-blue-600" />
                      <span className="font-medium text-blue-800">Signed in as {user.firstName} {user.lastName}</span>
                    </div>
                    <p className="text-blue-700 text-sm">Your subscription will be linked to {user.email}</p>
                  </div>
                ) : (
                  <div className="bg-yellow-50 border border-yellow-200 rounded-lg p-4">
                    <h3 className="font-semibold text-yellow-800 mb-2">Create Account Required</h3>
                    <p className="text-yellow-700 text-sm">
                      Please create a free account to manage your subscription and access premium features.
                    </p>
                  </div>
                )}
                
                <div className="bg-gradient-to-br from-indigo-50 to-purple-50 p-6 rounded-xl border border-indigo-100">
                  <h3 className="font-bold text-gray-800 mb-5 text-lg text-center">Unlock Your Full Potential with Pro</h3>
                  <div className="space-y-4">
                    {proFeatures.map((feature, index) => (
                      <div key={index} className="flex items-start gap-4">
                        <div className="bg-white p-2 rounded-full shadow-md mt-1 flex-shrink-0">
                          <SafeIcon icon={feature.icon} className="text-xl text-indigo-600" />
                        </div>
                        <div>
                          <h4 className="font-semibold text-gray-800">{feature.title}</h4>
                          <p className="text-sm text-gray-600">{feature.description}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>

                <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4 text-center">
                  <div className="text-3xl font-bold text-indigo-600 mb-1">$9.99 / month</div>
                  <div className="text-indigo-700 font-medium">Unlock Your Best Meals</div>
                  <div className="text-indigo-600 text-sm">Unlimited Premium Features • Cancel Anytime</div>
                </div>

                {paymentError && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-3 flex items-center gap-2 text-red-700">
                    <SafeIcon icon={FiAlertCircle} className="text-red-500 flex-shrink-0" />
                    <span className="text-sm">{paymentError}</span>
                  </div>
                )}

                <button
                  onClick={handleStripePayment}
                  disabled={isProcessing}
                  className="w-full bg-indigo-600 hover:bg-indigo-700 disabled:bg-indigo-400 text-white py-4 px-6 rounded-lg font-semibold text-lg transition-all duration-300 flex items-center justify-center gap-3"
                >
                  {isProcessing ? (
                    <>
                      <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent" />
                      <span>Redirecting to Stripe...</span>
                    </>
                  ) : !user ? (
                    <>
                      <SafeIcon icon={FiUser} className="text-xl" />
                      <span>Create Account to Upgrade</span>
                    </>
                  ) : (
                    <>
                      <SafeIcon icon={FiCreditCard} className="text-xl" />
                      <span>Pay with Stripe</span>
                    </>
                  )}
                </button>
                <div className="text-center text-xs text-gray-500">
                  Secure payment powered by Stripe • 30-day money-back guarantee
                </div>
              </div>
            </>
          )}
        </motion.div>
      </motion.div>
      <AuthModal
        isOpen={showAuthModal}
        onClose={() => setShowAuthModal(false)}
        mode={authMode}
        onSuccess={handleAuthSuccess}
        onModeChange={setAuthMode}
      />
    </>
  );
};

export default PaymentModal;